import type { Dashboard, NotificationList, MarkRead, UsageSummary } from '@inbound/contract';

import { apiSlice } from './api-slice.js';

/** How often the notification bell re-reads. Q8: 15 seconds, no SSE in v1. */
export const NOTIFICATION_POLL_MS = 15_000;

/**
 * How often the dashboard re-reads while its tab is visible.
 *
 * Inbound traffic is live: calls end and need a person with nothing to tell
 * the browser, so the page keeps up on its own (DV7).
 */
export const DASHBOARD_POLL_MS = 15_000;

/**
 * Dashboard and notifications (S11, S12).
 *
 * Both poll every 15 seconds: the dashboard while its tab is visible, the bell
 * always, because its whole purpose is to tell you about something you were
 * not looking for.
 */
export const dashboardApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getDashboard: build.query<Dashboard, void>({
      // Every tag it draws from, so an action taken elsewhere in the console
      // refreshes the numbers immediately rather than at the next poll.
      providesTags: ['Call', 'Interaction', 'Agent'],
      query: () => ({ url: '/dashboard' }),
    }),

    getUsage: build.query<UsageSummary, void>({
      query: () => ({ url: '/dashboard/usage' }),
      providesTags: ['Setting'],
    }),

    getNotifications: build.query<NotificationList, void>({
      query: () => ({ url: '/notifications' }),
      providesTags: ['Notification'],
    }),

    markNotificationsRead: build.mutation<{ read: number }, MarkRead>({
      query: (body) => ({ url: '/notifications/read', method: 'POST', body }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetUsageQuery,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} = dashboardApi;
